import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useContext } from 'react';
import { Routes, Route } from 'react-router-dom';
import SettingsPanel, { getPresetMeta } from './SettingsPanel';
import AdContainer from './AdContainer';
import Imprint from './Imprint';
import Footer from './Footer';
import Contact from './Contact';
import Privacy from './Privacy';
import About from './About';
import Header from './Header';
import SeoHead from './SeoHead';
import {
  SettingsProvider,
  SettingsContext,
  getThemeMeta,
  MANUAL_SKIP_CONTROLS_STORAGE_KEY,
  MANUAL_EXTENSION_CONTROLS_STORAGE_KEY,
  SIGNAL_VOLUME_STORAGE_KEY,

  SHOW_SHORTCUT_HINTS_STORAGE_KEY,
  DEFAULT_SIGNAL_VOLUME,
} from './SettingsContext';
import { LangProvider, useLang } from './LangContext';
import { getT } from './translations';

// Helper function: Format seconds as mm:ss
const formatTime = (sec) => {
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format an end timestamp (Unix seconds) as a locale-aware HH:MM / hh:MM AM/PM string.
 * Uses Intl.DateTimeFormat when available, falls back to 24h padded format.
 * @param {number} endTimestampSeconds - Unix timestamp in seconds
 * @param {string} [lang='en'] - BCP-47 language tag (e.g. 'en', 'de')
 * @returns {string}
 */
export function formatEndTime(endTimestampSeconds, lang = 'en') {
  const date = new Date(endTimestampSeconds * 1000);
  try {
    return new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit' }).format(date);
  } catch {
    const h = date.getHours().toString().padStart(2, '0');
    const m = date.getMinutes().toString().padStart(2, '0');
    return `${h}:${m}`;
  }
}

export function getSessionRemainingSeconds({
  phase,
  remaining,
  pomodoroTime,
  shortBreakTime,
  longBreakTime,
  currentCycleCount,
  numIntervals,
}) {
  if (phase === 'Pomodoro Flow' || remaining == null) {
    return null;
  }

  const clampedRemaining = Math.max(0, remaining);
  const clampedCycle = Math.max(1, currentCycleCount);
  const clampedIntervals = Math.max(1, numIntervals);

  if (phase === 'Long Break') {
    return clampedRemaining;
  }

  let total = clampedRemaining;

  if (phase === 'Countdown') {
    for (let cycle = clampedCycle; cycle <= clampedIntervals; cycle += 1) {
      total += pomodoroTime;
      total += cycle < clampedIntervals ? shortBreakTime : longBreakTime;
    }
    return total;
  }

  if (phase === 'Work') {
    for (let cycle = clampedCycle; cycle <= clampedIntervals; cycle += 1) {
      if (cycle > clampedCycle) {
        total += pomodoroTime;
      }
      total += cycle < clampedIntervals ? shortBreakTime : longBreakTime;
    }
    return total;
  }

  if (phase === 'Short Break') {
    for (let cycle = clampedCycle + 1; cycle <= clampedIntervals; cycle += 1) {
      total += pomodoroTime;
      total += cycle < clampedIntervals ? shortBreakTime : longBreakTime;
    }
    return total;
  }

  return clampedRemaining;
}

export function getBreakGuidance(phase, cycleCount = 1, overallCount = 1, t = getT('en')) {
  if (phase !== 'Short Break' && phase !== 'Long Break') {
    return null;
  }

  const rotationSeed = Math.max(0, (phase === 'Long Break' ? overallCount : cycleCount) - 1);
  const moment = t.breakMoments[rotationSeed % t.breakMoments.length];
  const kicker = phase === 'Long Break' ? t.kickerLongBreak : t.kickerShortBreak;
  const encouragement = phase === 'Long Break'
    ? t.breakEncouragementLong
    : t.breakEncouragementShort;

  return {
    kicker,
    encouragement,
    ...moment,
  };
}

function clampSignalVolume(volume) {
  const numericVolume = Number.parseFloat(volume);

  if (!Number.isFinite(numericVolume)) {
    return DEFAULT_SIGNAL_VOLUME;
  }

  return Math.min(1, Math.max(0, Number(numericVolume.toFixed(2))));
}

export function playSound(audioCtxRef, soundBufferRef, volume = DEFAULT_SIGNAL_VOLUME, activeSourceRef = null) {
  if (!audioCtxRef.current || !soundBufferRef.current) return null;
  const audioCtx = audioCtxRef.current;
  if (audioCtx.state === 'suspended') audioCtx.resume();

  if (activeSourceRef?.current) {
    try {
      activeSourceRef.current.stop();
    } catch (_) {
      // Ignore stale stop errors when replacing a previous preview instance.
    }
    activeSourceRef.current.disconnect?.();
    activeSourceRef.current = null;
  }

  const source = audioCtx.createBufferSource();
  const gainNode = audioCtx.createGain();
  source.buffer = soundBufferRef.current;
  gainNode.gain.value = clampSignalVolume(volume);
  source.connect(gainNode);
  gainNode.connect(audioCtx.destination);

  if (activeSourceRef) {
    activeSourceRef.current = source;
    source.onended = () => {
      if (activeSourceRef.current === source) {
        activeSourceRef.current = null;
      }
    };
  }

  source.start();
  return source;
}

function playTickSound(audioCtxRef, tickBufferRef, volume = DEFAULT_SIGNAL_VOLUME) {
  if (!audioCtxRef.current || !tickBufferRef.current) return;
  const audioCtx = audioCtxRef.current;
  if (audioCtx.state === 'suspended') audioCtx.resume();
  const source = audioCtx.createBufferSource();
  const gainNode = audioCtx.createGain();
  source.buffer = tickBufferRef.current;
  gainNode.gain.value = clampSignalVolume(volume);
  source.connect(gainNode);
  gainNode.connect(audioCtx.destination);
  source.start();
}

export function shouldShowManualPhaseControls(phase, isRunning, controlsEnabled) {
  return controlsEnabled && isRunning && phase !== 'Pomodoro Flow' && phase !== 'Countdown';
}

export function shouldWarnBeforeUnload(phase, isRunning, isPaused) {
  return isRunning && !isPaused && phase !== 'Pomodoro Flow';
}

export function getManualPhaseActionLabel(phase, t = getT('en')) {
  if (phase === 'Short Break' || phase === 'Long Break') {
    return t.endBreakNow;
  }

  if (phase === 'Work') {
    return t.skipPhase;
  }

  return t.skipPhase;
}

/**
 * Returns the number of pomodoros remaining (inclusive) until the next long break,
 * given the current 1-based cycle position and the configured interval count.
 *
 * Examples (numIntervals = 4):
 *   currentCycleCount=1 → 4 remaining  (long break after 3 more work phases)
 *   currentCycleCount=3 → 2 remaining
 *   currentCycleCount=4 → 1 remaining  (this one IS the last before long break)
 *
 * After a long break currentCycleCount resets to 1, so the indicator resets too.
 */
export function getPomodorosRemainingUntilLongBreak(currentCycleCount, numIntervals) {
  const clampedCycle = Math.max(1, currentCycleCount);
  const clampedIntervals = Math.max(1, numIntervals);
  return Math.max(0, clampedIntervals - clampedCycle + 1);
}

export function getCycleProgressState(phase, currentCycleCount, numIntervals) {
  const clampedIntervals = Math.max(1, numIntervals);
  const clampedCycle = Math.min(Math.max(1, currentCycleCount), clampedIntervals);

  if (phase === 'Long Break') {
    return {
      completedInCycle: clampedIntervals,
      remainingInCycle: 0,
    };
  }

  return {
    completedInCycle: Math.max(0, clampedCycle - 1),
    remainingInCycle: getPomodorosRemainingUntilLongBreak(clampedCycle, clampedIntervals),
  };
}

function HomeContent() {
  const { lang } = useLang();
  const t = getT(lang);
  return (
    <section className="home-content" aria-label={t.homeAriaLabel}>
      <div className="content-card hero-copy">
        <p className="eyebrow">{t.homeEyebrow}</p>
        <h1>{t.homeHero}</h1>
        <p className="lead">{t.homeLead}</p>
        <ul className="checklist compact-list">
          {t.homeHighlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>

      <article className="content-card">
        <h2>{t.homeFeatureIntroTitle}</h2>
        <p className="section-intro">{t.homeFeatureIntroLead}</p>
        <div className="content-grid two-col feature-grid">
          {t.homeFeatureCards.map((card) => (
            <section key={card.title} className="feature-card">
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <ul className="checklist compact-list feature-points">
                {card.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <p className="workflow-note"><strong>{t.homeWorkflowLabel}</strong> {card.workflow}</p>
            </section>
          ))}
        </div>
      </article>

      <article className="content-card">
        <h2>{t.homeHowTitle}</h2>
        <ol className="number-list">
          {t.homeHowSteps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </article>

      <article className="content-card">
        <h2>{t.homePomodoroTitle}</h2>
        <p className="section-intro">{t.homePomodoroLead}</p>
        <div className="content-grid two-col">
          <section>
            <h3>{t.homePomodoroHowItWorksTitle}</h3>
            <ol className="number-list compact-number-list">
              {t.homePomodoroHowItWorksSteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </section>
          <section>
            <h3>{t.homePomodoroWhyItHelpsTitle}</h3>
            <ul className="checklist compact-list">
              {t.homePomodoroBenefits.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>
        </div>
      </article>

      <div className="content-card">
        <h2>{t.homeFaqTitle}</h2>
        <div className="faq-list">
          {t.homeFaq.map((item) => (
            <details key={item.question}>
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function TimerPage() {
  const { lang } = useLang();
  const t = getT(lang);
  const isE2EDebug = process.env.REACT_APP_E2E_DEBUG === 'true';
  // Default settings (in minutes)
  const defaultPomodoroTime = parseInt(localStorage.getItem('pomodoroTime')) || 25;
  const defaultShortBreakTime = parseInt(localStorage.getItem('shortBreakTime')) || 5;
  const defaultLongBreakTime = parseInt(localStorage.getItem('longBreakTime')) || 30;
  const defaultIntervals = parseInt(localStorage.getItem('numIntervals')) || 4;
  const defaultCountdownEnabled = localStorage.getItem('enableCountdown') !== 'false';
  const defaultAutoStartPomodoros = localStorage.getItem('autoStartPomodoros') === 'true';
  const defaultPresetId = localStorage.getItem('timerPreset') || 'custom';

  const [activePresetId, setActivePresetId] = useState(defaultPresetId);

  // Convert minutes to seconds
  const [pomodoroTime, setPomodoroTime] = useState(defaultPomodoroTime * 60);
  const [shortBreakTime, setShortBreakTime] = useState(defaultShortBreakTime * 60);
  const [longBreakTime, setLongBreakTime] = useState(defaultLongBreakTime * 60);
  const [numIntervals, setNumIntervals] = useState(defaultIntervals);
  const [enableCountdown, setEnableCountdown] = useState(defaultCountdownEnabled);
  const [autoStartPomodoros, setAutoStartPomodoros] = useState(defaultAutoStartPomodoros);

  // Refs for real-time access during timer execution
  const pomodoroTimeRef = useRef(pomodoroTime);
  const shortBreakTimeRef = useRef(shortBreakTime);
  const longBreakTimeRef = useRef(longBreakTime);
  const numIntervalsRef = useRef(numIntervals);
  const autoStartPomodorosRef = useRef(autoStartPomodoros);

  useEffect(() => {
    pomodoroTimeRef.current = pomodoroTime;
    shortBreakTimeRef.current = shortBreakTime;
    longBreakTimeRef.current = longBreakTime;
    numIntervalsRef.current = numIntervals;
    autoStartPomodorosRef.current = autoStartPomodoros;
  }, [pomodoroTime, shortBreakTime, longBreakTime, numIntervals, autoStartPomodoros]);


  // Daily work counter — persists across reloads within the same calendar day.
  // Format in localStorage: { date: 'YYYY-MM-DD', count: N }
  const DAILY_WORK_COUNT_KEY = 'dailyWorkCount';
  const getTodayDateStr = () => new Date().toISOString().slice(0, 10);

  const loadDailyWorkCount = () => {
    try {
      const raw = localStorage.getItem(DAILY_WORK_COUNT_KEY);
      if (!raw) return 1;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.date === getTodayDateStr() && typeof parsed.count === 'number') {
        return parsed.count;
      }
    } catch (_) { /* ignore parse errors */ }
    return 1;
  };

  const saveDailyWorkCount = (count) => {
    localStorage.setItem(DAILY_WORK_COUNT_KEY, JSON.stringify({ date: getTodayDateStr(), count }));
  };

  // overallWorkCount tracks the total number of work phases (used when autoStart is enabled)
  const [overallWorkCount, setOverallWorkCount] = useState(() => loadDailyWorkCount());
  const overallWorkCountRef = useRef(overallWorkCount);

  // Persist the daily work count whenever it advances (survives page reload within the same day).
  useEffect(() => {
    saveDailyWorkCount(overallWorkCount);
  // saveDailyWorkCount is a stable inner function — no dep needed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overallWorkCount]);

  // currentCycleCount tracks the count within the current sequence (used when autoStart is disabled)
  const [currentCycleCount, setCurrentCycleCount] = useState(1);
  const currentCycleCountRef = useRef(currentCycleCount);

  const [hasCountdownOccurred, setHasCountdownOccurred] = useState(false);

  // phase can be: "Countdown", "Work", "Short Break", "Long Break", oder "Pomodoro Flow"
  const [phase, setPhase] = useState('Pomodoro Flow');
  const phaseRef = useRef(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const [remaining, setRemaining] = useState(pomodoroTime);
  // phaseEndTimestamp: Unix seconds when the current phase will end; null when timer is not running
  const [, setPhaseEndTimestamp] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const {
    showSettings,
    setShowSettings,
    activeThemeId,
    setActiveThemeId,
    manualSkipControlsEnabled,
    setManualSkipControlsEnabled,
    manualExtensionControlsEnabled,
    setManualExtensionControlsEnabled,

    signalVolume,
    setSignalVolume,
    showShortcutHints,
    setShowShortcutHints,
  } = useContext(SettingsContext);
  const [showAds, setShowAds] = useState(true);
  const [minimalMode, setMinimalMode] = useState(false);

  // countdownDuration must be declared before resetTimer (which depends on it in dep array)
  const countdownDuration = 10;

  // clearPausedTimerState must be declared before resetTimer (which depends on it)
  const clearPausedTimerState = useCallback(() => {
    pausedRemainingRef.current = 0;
    pausedElapsedRef.current = 0;
    setIsPaused(false);
  }, []);

  // resetTimer jetzt direkt nach den States/Refs, vor allen useCallback/useEffect-Hooks
  const resetTimer = useCallback((newPomodoroTime) => {
    cancelAnimationFrame(animationFrameId.current);
    setIsRunning(false);
    clearPausedTimerState();
    setPhase('Pomodoro Flow');
    setRemaining(newPomodoroTime);
    setPhaseEndTimestamp(null);
    // Reset cycle count; daily work count is preserved (reset doesn't undo completed sessions).
    setCurrentCycleCount(1);
    currentCycleCountRef.current = 1;
    setHasCountdownOccurred(false);
    lastTickRef.current = countdownDuration;
    const ring = document.getElementById('loading-ring-phase');
    if (ring) {
      ring.style.background = `conic-gradient(var(--loading-filled) 0deg, var(--loading-empty) 0deg)`;
    }
    document.title = `📚 ${formatTime(newPomodoroTime)} - Pomodoro Flow`;
  }, [clearPausedTimerState, countdownDuration, setCurrentCycleCount, setHasCountdownOccurred, setIsRunning, setPhase, setRemaining]);

  // Track screen size on mount only (not on every resize)
  useEffect(() => {
    // Keep the page behind the modal from scrolling, but avoid fixed-body locking,
    // which can break nested touch scrollers on iPhone-class browsers.
    const cls = 'settings-open';
    const html = document.documentElement;
    const body = document.body;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;

    if (showSettings) {
      html.classList.add(cls);
      body.classList.add(cls);
      html.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    } else {
      html.classList.remove(cls);
      body.classList.remove(cls);
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    }

    return () => {
      html.classList.remove(cls);
      body.classList.remove(cls);
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    };
  }, [showSettings]);

  useEffect(() => {
    if (!isE2EDebug || typeof window === 'undefined') return undefined;

    const readRect = (id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        x: Math.round(r.x),
        y: Math.round(r.y),
        width: Math.round(r.width),
        height: Math.round(r.height),
        top: Math.round(r.top),
        right: Math.round(r.right),
        bottom: Math.round(r.bottom),
        left: Math.round(r.left),
      };
    };

    const readStyle = (id) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const style = window.getComputedStyle(el);
      return {
        display: style.display,
        visibility: style.visibility,
        opacity: style.opacity,
        pointerEvents: style.pointerEvents,
        zIndex: style.zIndex,
      };
    };

    window.__PF_DEBUG__ = {
      getState: () => ({
        showSettings,
        showAds,
        phase,
        isRunning,
        isPaused,
        bodyClassName: document.body.className,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        },
        stickyAdHeightVar: getComputedStyle(document.documentElement).getPropertyValue('--sticky-ad-h').trim(),
        timerPagePaddingBottom: document.querySelector('.timer-page')
          ? getComputedStyle(document.querySelector('.timer-page')).paddingBottom
          : null,
        openSettingsRect: readRect('open-settings'),
        openSettingsStyle: readStyle('open-settings'),
        settingsPanelRect: readRect('settings-panel'),
        settingsPanelStyle: readStyle('settings-panel'),
        adRect: readRect('ad-container'),
        adStyle: readStyle('ad-container'),
      }),
    };

    return () => {
      delete window.__PF_DEBUG__;
    };
  }, [isE2EDebug, showSettings, showAds, phase, isRunning, isPaused]);

  const isRunningRef = useRef(isRunning);
  useEffect(() => {
    isRunningRef.current = isRunning;
  }, [isRunning]);
  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (!shouldWarnBeforeUnload(phase, isRunning, isPaused)) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPaused, isRunning, phase]);

  const startTimeRef = useRef(0);
  const clockStartRef = useRef(0);
  const endTimeRef = useRef(0);
  const animationFrameId = useRef(null);
  const pausedRemainingRef = useRef(0);
  const pausedElapsedRef = useRef(0);
  const lastTickRef = useRef(countdownDuration);

  const transitioning = useRef(false);

  const audioCtxRef = useRef(null);
  const soundBufferRef = useRef(null);
  const tickBufferRef = useRef(null);
  const previewSoundSourceRef = useRef(null);
  const cueTimeoutsRef = useRef([]);
  const getCurrentTime = () => {
    // Always use Date.now()/1000 as the clock source.
    //
    // We previously used audioCtx.currentTime when the AudioContext was in the
    // 'running' state, but this causes a catastrophic clock-source switch on iOS
    // Safari: the timer starts while the AudioContext is still suspended (so
    // endTimeRef is set in Unix-epoch seconds via Date.now()/1000 ≈ 1.7 × 10⁹),
    // then the context resumes asynchronously and its currentTime resets to ~0.
    // The next tick reads getCurrentTime() → audioCtx.currentTime ≈ 5, computes
    // rem = endTimeRef − 5 ≈ 1.7 × 10⁹, and displays hundreds of thousands of
    // seconds. Using Date.now()/1000 unconditionally eliminates the clock mismatch.
    //
    // audioCtx.currentTime is only needed for scheduling Web Audio API events
    // (sounds, worklets) — those call audioCtxRef.current.currentTime directly
    // and are unaffected by this change.
    return Date.now() / 1000;
  };

  useEffect(() => {
    document.body.classList.remove('theme-work', 'theme-break');
    if (phase === 'Short Break' || phase === 'Long Break') {
      document.body.classList.add('theme-break');
    } else {
      document.body.classList.add('theme-work');
    }
    return () => {
      document.body.classList.remove('theme-work', 'theme-break');
    };
  }, [phase]);

  useEffect(() => {
    // Skip audio initialization in test environment
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    async function loadSoundAndKeepContextAlive() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioContext();
        audioCtxRef.current = audioCtx;
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.frequency.value = 0;
        gainNode.gain.value = 0;
        oscillator.start();

        const response = await fetch(`${process.env.PUBLIC_URL}/alarm_clock.mp3`);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        soundBufferRef.current = audioBuffer;

        const tickResponse = await fetch(`${process.env.PUBLIC_URL}/tick_tack.wav`);
        const tickArrayBuffer = await tickResponse.arrayBuffer();
        const tickAudioBuffer = await audioCtx.decodeAudioData(tickArrayBuffer);
        tickBufferRef.current = tickAudioBuffer;
      } catch (err) {
        console.error('Error loading sounds or starting oscillator', err);
      }
    }
    loadSoundAndKeepContextAlive();
  }, []);

  useEffect(() => {
    document.title = `📚 ${formatTime(remaining)} - ${phase}`;
  }, [remaining, phase]);

  const clearCueTimeouts = useCallback(() => {
    cueTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    cueTimeoutsRef.current = [];
  }, []);

  const triggerPhaseCompletionCue = useCallback((nextPhase) => {
    // Tab visible → single bell. Tab hidden → 2 bells so the user notices.
    const cueCount = document.hidden ? 2 : 1;
    const cueSpacingMs = 1800;

    clearCueTimeouts();

    for (let index = 0; index < cueCount; index += 1) {
      const timeoutId = window.setTimeout(() => {
        playSound(audioCtxRef, soundBufferRef, signalVolume);
      }, index * cueSpacingMs);
      cueTimeoutsRef.current.push(timeoutId);
    }

    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      const vibrationPattern = document.hidden ? [250, 140, 250] : [200];
      navigator.vibrate(vibrationPattern);
    }

  }, [clearCueTimeouts, signalVolume]);

  useEffect(() => () => {
    clearCueTimeouts();
  }, [clearCueTimeouts]);

  const setupTitleUpdater = useCallback(() => {
    if (!audioCtxRef.current) return;
    audioCtxRef.current.audioWorklet
      .addModule(`${process.env.PUBLIC_URL}/titleUpdater.worklet.js`)
      .then(() => {
        const workletNode = new AudioWorkletNode(audioCtxRef.current, 'title-updater');
        workletNode.port.onmessage = () => {
          if (!isRunningRef.current || isPausedRef.current) return;
          const now = getCurrentTime();
          const rem = Math.max(0, endTimeRef.current - now);
          const secondsRemaining = Math.ceil(rem);
          document.title = `📚 ${formatTime(secondsRemaining)} - ${phaseRef.current}`;
        };
        workletNode.connect(audioCtxRef.current.destination);
      })
      .catch((err) => console.error('AudioWorklet load failed:', err));
  }, []);

  useEffect(() => {
    setupTitleUpdater();
  }, [setupTitleUpdater]);

  // Diese Funktionen haben zirkuläre Abhängigkeiten (tick → handlePhaseTransition → startNewPhase → tick)
  // ESLint Regeln werden hier deaktiviert, da die Logik über refs korrekt funktioniert

  // eslint-disable-next-line no-use-before-define
  const tick = useCallback(() => {
    const now = getCurrentTime();
    const rem = Math.max(0, endTimeRef.current - now);
    const secondsRemaining = Math.ceil(rem);
    setRemaining(secondsRemaining);
    const total = endTimeRef.current - clockStartRef.current;
    const elapsed = total - rem;
    const angle = (elapsed / total) * 360;
    const ring = document.getElementById('loading-ring-phase');
    if (ring) {
      ring.style.background = `conic-gradient(var(--loading-filled) ${angle}deg, var(--loading-empty) 0deg)`;
    }
    if (rem <= 0) {
      cancelAnimationFrame(animationFrameId.current);
      handlePhaseTransition();
    } else {
      animationFrameId.current = requestAnimationFrame(tick);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNewPhase = useCallback((duration, { startPaused = false } = {}) => {
    const now = getCurrentTime();
    clockStartRef.current = now;
    endTimeRef.current = now + duration;
    setRemaining(duration);
    setPhaseEndTimestamp(now + duration);

    if (startPaused) {
      pausedRemainingRef.current = duration;
      pausedElapsedRef.current = 0;
      setIsPaused(true);
      return;
    }

    clearPausedTimerState();
    animationFrameId.current = requestAnimationFrame(tick);
  }, [clearPausedTimerState, tick]);

  // Revised phase transition logic using separate counters.
  const handlePhaseTransition = useCallback(() => {
    if (transitioning.current) return;
    transitioning.current = true;

    let nextPhase = '';
    let nextDuration = 0;
    let adVisible = false;

    if (phaseRef.current === 'Countdown') {
      nextPhase = 'Work';
      nextDuration = pomodoroTimeRef.current;
      // Do not increment counters on countdown.
    } else if (phaseRef.current === 'Work') {
      // If the current work phase in this cycle is less than intervals, then next is short break;
      // otherwise, it’s the last work phase in the cycle, so next is long break.
      if (currentCycleCountRef.current < numIntervalsRef.current) {
        nextPhase = 'Short Break';
        nextDuration = shortBreakTimeRef.current;
      } else {
        nextPhase = 'Long Break';
        nextDuration = longBreakTimeRef.current;
        adVisible = true;
      }
    } else if (phaseRef.current === 'Short Break') {
      // After a short break, increment both counters.
      const newOverall = overallWorkCountRef.current + 1;
      setOverallWorkCount(newOverall);
      overallWorkCountRef.current = newOverall;
      const newCycle = currentCycleCountRef.current + 1;
      setCurrentCycleCount(newCycle);
      currentCycleCountRef.current = newCycle;
      nextPhase = 'Work';
      nextDuration = pomodoroTimeRef.current;
    } else if (phaseRef.current === 'Long Break') {
      // After a long break, increment overall count and reset cycle count.
      // Only auto-continue if autoStartPomodoros is enabled
      if (autoStartPomodorosRef.current) {
        const newOverall = overallWorkCountRef.current + 1;
        setOverallWorkCount(newOverall);
        overallWorkCountRef.current = newOverall;
        setCurrentCycleCount(1);
        currentCycleCountRef.current = 1;
        nextPhase = 'Work';
        nextDuration = pomodoroTimeRef.current;
      } else {
        // Auto Start OFF: Stop the timer and play alarm
        triggerPhaseCompletionCue('Pomodoro Flow');
        resetTimer(pomodoroTimeRef.current);
        transitioning.current = false;
        return;
      }
    } else {
      nextPhase = 'Pomodoro Flow';
    }

    phaseRef.current = nextPhase;
    setPhase(nextPhase);
    setShowAds(adVisible);
    if (phaseRef.current !== 'Long Break' || autoStartPomodorosRef.current) {
      triggerPhaseCompletionCue(nextPhase);
    }
    startNewPhase(nextDuration, { startPaused: isPausedRef.current });
    setTimeout(() => {
      transitioning.current = false;
    }, 0);
  }, [startNewPhase, triggerPhaseCompletionCue, resetTimer]);

  const animateCountdown = useCallback(() => {
    const now = getCurrentTime();
    const rem = Math.max(0, endTimeRef.current - now);
    const secondsRemaining = Math.ceil(rem);
    setRemaining(secondsRemaining);
    const currentSec = Math.floor(rem);
    if (currentSec < lastTickRef.current) {
      playTickSound(audioCtxRef, tickBufferRef, signalVolume);
      lastTickRef.current = currentSec;
    }
    const ring = document.getElementById('loading-ring-phase');
    if (ring) {
      const angle = ((countdownDuration - rem) / countdownDuration) * 360;
      ring.style.background = `conic-gradient(var(--loading-filled) ${angle}deg, var(--loading-empty) 0deg)`;
    }
    document.title = `📚 ${formatTime(secondsRemaining)} - Countdown`;
    if (rem > 0) {
      animationFrameId.current = requestAnimationFrame(animateCountdown);
    } else {
      setPhase('Work');
      triggerPhaseCompletionCue('Work');
      setShowAds(false);
      startNewPhase(pomodoroTimeRef.current);
    }
  }, [countdownDuration, signalVolume, startNewPhase, triggerPhaseCompletionCue]);

  useEffect(() => {
    let fallbackInterval = null;
    const updateFallback = () => {
      if (isRunning && !isPaused) {
        const now = getCurrentTime();
        const rem = Math.max(0, endTimeRef.current - now);
        const secondsRemaining = Math.ceil(rem);
        setRemaining(secondsRemaining);
        document.title = `📚 ${formatTime(secondsRemaining)} - ${phaseRef.current}`;
        if (rem <= 0) {
          cancelAnimationFrame(animationFrameId.current);
          handlePhaseTransition();
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        fallbackInterval = setInterval(updateFallback, 250);
      } else {
        if (fallbackInterval) {
          clearInterval(fallbackInterval);
          fallbackInterval = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (fallbackInterval) clearInterval(fallbackInterval);
    };
  }, [isRunning, isPaused, handlePhaseTransition, resetTimer]);

  const startTimer = useCallback(() => {
    // Resume AudioContext on user gesture (iOS Safari requires this). resume() is async
    // but we do not await it here — the timer clock falls back to Date.now()/1000 via
    // getCurrentTime() while the context is still suspended, so timing stays correct.
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume().catch(() => {});
    }
    if (!isRunning) {
      setIsRunning(true);
      if (enableCountdown && !hasCountdownOccurred) {
        setPhase('Countdown');
        setHasCountdownOccurred(true);
        const now = getCurrentTime();
        startTimeRef.current = now;
        endTimeRef.current = now + countdownDuration;
        setPhaseEndTimestamp(now + countdownDuration);
        animationFrameId.current = requestAnimationFrame(animateCountdown);
      } else {
        setPhase('Work');
        setShowAds(false);
        startNewPhase(pomodoroTime);
      }
    } else if (isPaused) {
      const now = getCurrentTime();
      endTimeRef.current = now + pausedRemainingRef.current;
      clockStartRef.current = now - pausedElapsedRef.current;
      setPhaseEndTimestamp(endTimeRef.current);
      setIsPaused(false);
      if (phase === 'Countdown') {
        animationFrameId.current = requestAnimationFrame(animateCountdown);
      } else {
        animationFrameId.current = requestAnimationFrame(tick);
      }
    }
  }, [
    animateCountdown,
    countdownDuration,
    enableCountdown,
    hasCountdownOccurred,
    isPaused,
    isRunning,
    phase,
    pomodoroTime,
    startNewPhase,
    tick,
  ]);

  const pauseTimer = useCallback(() => {
    if (isRunning && !isPaused) {
      const now = getCurrentTime();
      pausedRemainingRef.current = endTimeRef.current - now;
      pausedElapsedRef.current = now - clockStartRef.current;
      setIsPaused(true);
      cancelAnimationFrame(animationFrameId.current);
    }
  }, [isPaused, isRunning]);




  const handleStartPause = useCallback(() => {
    if (!isRunning || isPaused) {
      startTimer();
    } else {
      pauseTimer();
    }
  }, [isRunning, isPaused, startTimer, pauseTimer]);

  const handleReset = useCallback(() => {
    resetTimer(pomodoroTime);
  }, [pomodoroTime, resetTimer]);

  const adjustRemainingTime = useCallback((deltaSeconds) => {
    if (!shouldShowManualPhaseControls(phase, isRunning, manualExtensionControlsEnabled)) {
      return;
    }

    setRemaining((currentRemaining) => {
      const nextRemaining = Math.max(1, currentRemaining + deltaSeconds);

      if (isPaused) {
        pausedRemainingRef.current = nextRemaining;
      } else {
        endTimeRef.current += deltaSeconds;
        setPhaseEndTimestamp(endTimeRef.current);
      }

      return nextRemaining;
    });
  }, [isPaused, isRunning, manualExtensionControlsEnabled, phase]);

  const handleManualPhaseAdvance = useCallback(() => {
    if (!shouldShowManualPhaseControls(phase, isRunning, manualSkipControlsEnabled)) {
      return;
    }

    cancelAnimationFrame(animationFrameId.current);
    clearCueTimeouts();
    transitioning.current = false;
    handlePhaseTransition();
  }, [clearCueTimeouts, handlePhaseTransition, isRunning, manualSkipControlsEnabled, phase]);

  useLayoutEffect(() => {
    const isTypingTarget = (target) => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tagName = target.tagName;
      return target.isContentEditable || tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT';
    };

    const handleKeydown = (event) => {
      const normalizedKey = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      const isSpaceKey = event.code === 'Space' || event.key === ' ' || normalizedKey === 'spacebar';
      const isResetKey = event.code === 'KeyR' || normalizedKey === 'r';
      const isSettingsKey = event.code === 'KeyS' || normalizedKey === 's';
      const isHintsKey = event.code === 'KeyH' || normalizedKey === 'h';
      const isFocusKey = event.code === 'KeyF' || normalizedKey === 'f';
      const isEscapeKey = event.code === 'Escape' || normalizedKey === 'escape';
      if (event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }

      if (isTypingTarget(event.target)) {
        return;
      }

      if (isSpaceKey) {
        event.preventDefault();
        handleStartPause();
        return;
      }

      if (isResetKey) {
        event.preventDefault();
        handleReset();
        return;
      }

      if (isSettingsKey) {
        event.preventDefault();
        setShowSettings(true);
        return;
      }

      if (isHintsKey) {
        event.preventDefault();
        setShowShortcutHints((prev) => !prev);
        return;
      }

      if (isFocusKey) {
        if (showSettings) return;
        event.preventDefault();
        setMinimalMode((prev) => !prev);
        return;
      }

      if (isEscapeKey) {
        if (minimalMode) {
          event.preventDefault();
          setMinimalMode(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [minimalMode, handleReset, handleStartPause, setShowSettings, setShowShortcutHints, showSettings]);

  // On iOS Safari, onTouchEnd fires first and then the browser synthesises a click
  // ~300 ms later. Using event.preventDefault() in onTouchEnd suppresses the click in
  // most cases, but React's synthetic event system can still dispatch onClick in the
  // same flush before the native preventDefault takes effect. Guard with a ref so that
  // a touch-triggered handler call always wins and the subsequent synthetic click is
  // ignored. Falls back gracefully to onClick-only on pointer devices.
  const lastTapByTouchRef = useRef(false);
  const bindTap = useCallback((handler) => ({
    onClick: (event) => {
      if (lastTapByTouchRef.current) {
        // This onClick was triggered by the touch-to-click synthesis — skip it.
        lastTapByTouchRef.current = false;
        return;
      }
      handler(event);
    },
    onTouchEnd: (event) => {
      event.preventDefault();
      lastTapByTouchRef.current = true;
      handler();
    },
  }), []);

  const breakGuidance = getBreakGuidance(phase, currentCycleCount, overallWorkCount, t);
  const showSkipControls = shouldShowManualPhaseControls(phase, isRunning, manualSkipControlsEnabled);
  const showExtensionControls = shouldShowManualPhaseControls(phase, isRunning, manualExtensionControlsEnabled);
  const showManualControls = showSkipControls || showExtensionControls;
  const manualPhaseActionLabel = getManualPhaseActionLabel(phase, t);
  const displayedPhaseEndTimestamp = phase !== 'Pomodoro Flow' && isRunning
    ? getCurrentTime() + remaining
    : null;
  const sessionRemainingSeconds = getSessionRemainingSeconds({
    phase,
    remaining,
    pomodoroTime,
    shortBreakTime,
    longBreakTime,
    currentCycleCount,
    numIntervals,
  });
  const sessionEndTimestamp = sessionRemainingSeconds !== null && isRunning
    ? getCurrentTime() + sessionRemainingSeconds
    : null;

  // Pip progress indicator: show when session is active (any phase except idle 'Pomodoro Flow')
  const showPipIndicator = phase !== 'Pomodoro Flow';
  const { completedInCycle, remainingInCycle } = getCycleProgressState(
    phase,
    currentCycleCount,
    numIntervals,
  );

  // getPhaseHeading returns different formats based on autoStartPomodoros.
  // When auto-start is ON during a Work phase, shows the daily counter as the
  // primary label and appends a muted cycle-position suffix (e.g. "· 2 / 4").
  const getPhaseHeading = () => {
    if (phase === 'Countdown') {
      return t.phaseCountdown;
    } else if (phase === 'Work') {
      if (autoStartPomodoros) {
        return (
          <>
            {`${t.phaseWorkTodayPrefix} ${overallWorkCount}`}
            <span className="phase-heading-cycle">
              {` · ${currentCycleCount}\u202f/\u202f${numIntervals}`}
            </span>
          </>
        );
      } else {
        return `${t.phaseWork} ${currentCycleCount}/${numIntervals}`;
      }
    } else if (phase === 'Short Break') {
      return t.phaseShortBreak;
    } else if (phase === 'Long Break') {
      return t.phaseLongBreak;
    } else {
      return t.phasePomodoroFlow;
    }
  };

  return (
    <div className={`timer-page${minimalMode ? ' minimal-player-mode' : ''}`}>
      <div className="timer-shell">
        <div
          id="phase-heading"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            marginBottom: '2rem',
            fontSize: '2rem',
            fontWeight: 'bold',
          }}
        >
          {getPhaseHeading()}
        </div>
        <div className="active-meta" aria-label="Active timer details">
          <p className="active-preset" aria-label="Active timer preset">
            {t.presetLabel} <strong>{getPresetMeta(activePresetId).name}</strong>
          </p>
          <p className="active-theme" aria-label="Active color theme">
            {t.themeLabel} <strong>{getThemeMeta(activeThemeId).name}</strong>
          </p>
        </div>

        {breakGuidance && (
          <section className="break-guidance" aria-label="Break guidance">
            <p className="break-guidance-kicker">{breakGuidance.kicker}</p>
            <div className="break-guidance-row">
              <strong>{breakGuidance.label}</strong>
              <span>{breakGuidance.title}</span>
            </div>
            <p className="break-guidance-body">{breakGuidance.body}</p>
            <p className="break-guidance-encouragement">{breakGuidance.encouragement}</p>
          </section>
        )}

        <div className="timer-stage" data-testid="timer-stage">
          <div id="timer-container">
            <div id="loading-ring-phase" style={{ width: '100%', height: '100%' }}></div>
            <div id="inner-ring-phase"></div>
            <div id="timer-display">{formatTime(remaining)}</div>
          </div>
        </div>

        {showPipIndicator && (
          <div
            className="pomodoro-pip-indicator"
            role="img"
            aria-label={t.pomodoroProgressAria(remainingInCycle, numIntervals)}
            data-testid="pip-indicator"
          >
            {Array.from({ length: numIntervals }, (_, i) => (
              <span
                key={i}
                className={`pip${i < completedInCycle ? ' pip-done' : ''}`}
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {displayedPhaseEndTimestamp !== null && (
          <div className="phase-end-times" aria-label="Timer end time estimates">
            <p
              className="phase-end-time"
              aria-label={`${t.phaseEndsAt} ${formatEndTime(displayedPhaseEndTimestamp, lang)}`}
            >
              {t.phaseEndsAt} {formatEndTime(displayedPhaseEndTimestamp, lang)}
            </p>
            {sessionEndTimestamp !== null && (
              <p
                className="session-end-time"
                aria-label={`${t.sessionEndsAt} ${formatEndTime(sessionEndTimestamp, lang)}`}
              >
                {t.sessionEndsAt} {formatEndTime(sessionEndTimestamp, lang)}
              </p>
            )}
          </div>
        )}

        <div id="controls-container">
          <button id="start-btn" type="button" {...bindTap(handleStartPause)}>
            {!isRunning || isPaused ? t.start : t.pause}
          </button>
          <button id="reset-btn" type="button" {...bindTap(handleReset)}>
            {t.reset}
          </button>
        </div>

        {!minimalMode && (
          <section className="mobile-quick-actions" aria-label={t.quickActions}>
            <div className="mobile-quick-actions-buttons">
              <button
                type="button"
                className="mobile-quick-action-btn mobile-quick-action-btn-primary"
                {...bindTap(() => setMinimalMode(true))}
              >
                {t.enterMinimalPlayerAction}
              </button>
            </div>
          </section>
        )}

        {showManualControls && (
          <section className="manual-controls" aria-label="Manual timer controls">
            <p className="manual-controls-label">{t.quickAdjust}</p>
            <div className="manual-controls-actions">
              {showSkipControls && (
                <button type="button" className="manual-btn manual-btn-skip" {...bindTap(handleManualPhaseAdvance)}>
                  {manualPhaseActionLabel}
                </button>
              )}
              {showExtensionControls && (
                <>
                  <button type="button" className="manual-btn manual-btn-extend" {...bindTap(() => adjustRemainingTime(60))}>
                    +1 min
                  </button>
                  <button type="button" className="manual-btn manual-btn-extend" {...bindTap(() => adjustRemainingTime(5 * 60))}>
                    +5 min
                  </button>
                </>
              )}
            </div>
          </section>
        )}

        {showShortcutHints && !minimalMode && (!isRunning || isPaused) && (
          <div className="shortcut-hint" aria-label="Keyboard shortcuts">
            <span className="shortcut-hint-label">{lang === 'de' ? 'Tastenkürzel:' : 'Shortcuts:'}</span>
            <span className="shortcut-hint-list">
              <span className="shortcut-item"><kbd className="keycap keycap-space">Space</kbd><span>{lang === 'de' ? 'Start/Pause' : 'start/pause'}</span></span>
              <span className="shortcut-separator" aria-hidden="true">·</span>
              <span className="shortcut-item"><kbd className="keycap">R</kbd><span>{lang === 'de' ? 'Zurücksetzen' : 'reset'}</span></span>
              <span className="shortcut-separator" aria-hidden="true">·</span>
              <span className="shortcut-item"><kbd className="keycap">S</kbd><span>{lang === 'de' ? 'Einstellungen' : 'settings'}</span></span>
              <span className="shortcut-separator" aria-hidden="true">·</span>
              <span className="shortcut-item"><kbd className="keycap">F</kbd><span>{lang === 'de' ? 'Minimalplayer' : 'minimalist player'}</span></span>
              <span className="shortcut-separator" aria-hidden="true">·</span>
              <span className="shortcut-item"><kbd className="keycap">H</kbd><span>{lang === 'de' ? 'Hinweise' : 'hints'}</span></span>
            </span>
          </div>
        )}

        {minimalMode && (
          <button
            type="button"
            className="minimal-player-exit-chip"
            aria-label={t.exitMinimalPlayerAction}
            onClick={() => setMinimalMode(false)}
          >
            {lang === 'de' ? '[Esc] Minimalplayer beenden' : '[Esc] exit minimalist player'}
          </button>
        )}
      </div>

      {showSettings && (
        <div className="settings-overlay" role="dialog" aria-modal="true" aria-label="Settings">
          <SettingsPanel
            pomodoroTime={pomodoroTime / 60}
            shortBreakTime={shortBreakTime / 60}
            longBreakTime={longBreakTime / 60}
            numIntervals={numIntervals}
            enableCountdown={enableCountdown}
            autoStartPomodoros={autoStartPomodoros}
            manualSkipControlsEnabled={manualSkipControlsEnabled}
            manualExtensionControlsEnabled={manualExtensionControlsEnabled}
            showShortcutHints={showShortcutHints}
            activeThemeId={activeThemeId}
            signalVolume={signalVolume}
            onTestSound={(previewVolume) => {
              playSound(audioCtxRef, soundBufferRef, previewVolume, previewSoundSourceRef);
            }}
            onApply={(
              newPomodoro,
              newShortBreak,
              newLongBreak,
              newIntervals,
              newCountdown,
              newAutoStart,
              newManualSkipControlsEnabled,
              newManualExtensionControlsEnabled,
              newPresetId,
              newThemeId,
              newSignalVolume,
              newShowShortcutHints
            ) => {
              const newPomodoroTimeSec = newPomodoro * 60;
              const newShortBreakTimeSec = newShortBreak * 60;
              const newLongBreakTimeSec = newLongBreak * 60;
              const timerStructureChanged = (
                pomodoroTime !== newPomodoroTimeSec
                || shortBreakTime !== newShortBreakTimeSec
                || longBreakTime !== newLongBreakTimeSec
                || numIntervals !== newIntervals
              );

              setPomodoroTime(newPomodoroTimeSec);
              setShortBreakTime(newShortBreakTimeSec);
              setLongBreakTime(newLongBreakTimeSec);
              setNumIntervals(newIntervals);
              setEnableCountdown(newCountdown);
              setAutoStartPomodoros(newAutoStart);
              setManualSkipControlsEnabled(newManualSkipControlsEnabled);
              setManualExtensionControlsEnabled(newManualExtensionControlsEnabled);
              setShowShortcutHints(newShowShortcutHints);
              setActivePresetId(newPresetId);
              setActiveThemeId(newThemeId);
              setSignalVolume(clampSignalVolume(newSignalVolume));
              localStorage.setItem('pomodoroTime', newPomodoro);
              localStorage.setItem('shortBreakTime', newShortBreak);
              localStorage.setItem('longBreakTime', newLongBreak);
              localStorage.setItem('numIntervals', newIntervals);
              localStorage.setItem('enableCountdown', newCountdown);
              localStorage.setItem('autoStartPomodoros', newAutoStart);
              localStorage.setItem(MANUAL_SKIP_CONTROLS_STORAGE_KEY, newManualSkipControlsEnabled);
              localStorage.setItem(MANUAL_EXTENSION_CONTROLS_STORAGE_KEY, newManualExtensionControlsEnabled);
              localStorage.setItem(SHOW_SHORTCUT_HINTS_STORAGE_KEY, String(newShowShortcutHints));
              localStorage.setItem(SIGNAL_VOLUME_STORAGE_KEY, String(clampSignalVolume(newSignalVolume)));
              localStorage.setItem('timerPreset', newPresetId);
              if (timerStructureChanged) {
                resetTimer(newPomodoroTimeSec);
              }
              setShowSettings(false);
            }}
            onClose={() => setShowSettings(false)}
          />
        </div>
      )}

      <HomeContent />

      <AdContainer
        visible={showAds && !showSettings}
        onClose={() => setShowAds(false)}
        disableClose={false}
      />
    </div>
  );
}

function App() {
  return (
    <SettingsProvider>
      <SeoHead />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Header />
        <main style={{ flex: 1 }}>
          <Routes>
            <Route path="/" element={<TimerPage />} />
            <Route path="/index.html" element={<TimerPage />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </SettingsProvider>
  );
}

export default function AppWithProviders() {
  return (
    <LangProvider>
      <App />
    </LangProvider>
  );
}
